import {Injectable} from '@angular/core';
import {Action, Store} from '@ngrx/store';
import {Actions, Effect, ofType} from '@ngrx/effects';

import {Observable, of} from 'rxjs';

import {EventService, IOrgMinAvg} from '../../../dashboard/shared';
import * as avgAirData from '../actions/avg-air-data.action';
import * as fromRoot from '../../../reducers';
import {catchError, concatMap, map, switchMap, withLatestFrom} from 'rxjs/internal/operators';

@Injectable()
export class AvgAirDataEffects {

  @Effect()
  updateTimeRange$: Observable<Action> = this.actions$.pipe(
    ofType(avgAirData.UPDATE_TIME_RANGE),
    withLatestFrom(this.state$),
    map(([action, state]: [avgAirData.UpdateTimeRangeAction, fromRoot.State]) => {
      const payload = <{ orgToken: string, fromDate: Date, toDate: Date }>action.payload;
      const groupId: string | null = state.avgAirData.selectedGroupId;

      if (groupId == null) {
        return new avgAirData.RequestOrgAvgAction({
          orgToken: payload.orgToken,
          interval: state.avgAirData.interval,
          fromDate: payload.fromDate,
          toDate: payload.toDate
        });
      } else {
        return new avgAirData.RequestGroupAvgAction({
          orgToken: payload.orgToken,
          groupId: groupId,
          interval: state.avgAirData.interval,
          fromDate: payload.fromDate,
          toDate: payload.toDate
        });
      }
    }),
    catchError(() => of(new avgAirData.UpdateTimeRangeFailAction()))
  );

  @Effect()
  updateAvg$: Observable<Action> = this.actions$.pipe(
    ofType(avgAirData.UPDATE_AVG),
    withLatestFrom(this.state$),
    map(([action, state]: [avgAirData.UpdateAvgAction, fromRoot.State]) => {
      const payload = <{ orgToken: string, groupId: string, interval: number, fromDate: Date, toDate: Date }>action.payload;
      const groupId: string | null = payload.groupId;

      // Need to use concatMap next to set loading state
      if (groupId === null) {
        return [
          new avgAirData.StartLoadingAction(),
          new avgAirData.RequestOrgAvgAction({
            orgToken: payload.orgToken,
            interval: payload.interval,
            fromDate: payload.fromDate,
            toDate: payload.toDate
          })
        ];
      } else {
        return [
          new avgAirData.StartLoadingAction(),
          new avgAirData.RequestGroupAvgAction({
            orgToken: payload.orgToken,
            groupId: groupId,
            interval: payload.interval,
            fromDate: payload.fromDate,
            toDate: payload.toDate
          })
        ];
      }
    }),
    concatMap((actionList: any[]) => actionList),
    catchError(() => of(new avgAirData.RequestFailAction()))
  );

  @Effect()
  requestOrgAvg$: Observable<Action> = this.actions$.pipe(
    ofType(avgAirData.REQUEST_ORG_AVG),
    withLatestFrom(this.state$),
    switchMap(([action, state]: [avgAirData.RequestOrgAvgAction, fromRoot.State]) => {
      const payload = <{
        orgToken: string,
        interval: number,
        fromDate: Date,
        toDate: Date,
        desc?: boolean,
        limit?: number
      }>action.payload;

      return this.eventService.requestOrgMinAvg(payload.orgToken, payload.interval, payload.fromDate, payload.toDate).pipe(
        map((orgMinAvgList: IOrgMinAvg[]) => {

          return new avgAirData.RequestSuccessAction({
            selectedGroupId: null,
            avgAirData: orgMinAvgList,
            timeRange: {fromDate: payload.fromDate, toDate: payload.toDate}
          });
        })
      );
    }),
    catchError(() => of(new avgAirData.RequestFailAction()))
  );

  @Effect()
  requestGroupAvg$: Observable<Action> = this.actions$.pipe(
    ofType(avgAirData.REQUEST_GROUP_AVG),
    withLatestFrom(this.state$),
    switchMap(([action, state]: [avgAirData.RequestGroupAvgAction, fromRoot.State]) => {
      const payload = <{
        orgToken: string,
        groupId: string,
        interval: number,
        fromDate: Date,
        toDate: Date,
        desc?: boolean,
        limit?: number
      }>action.payload;

      return this.eventService.requestGroupMinAvg(payload.orgToken, payload.groupId, payload.interval, payload.fromDate, payload.toDate)
        .pipe(
          map((orgMinAvgList: IOrgMinAvg[]) => {

            return new avgAirData.RequestSuccessAction({
              selectedGroupId: payload.groupId,
              avgAirData: orgMinAvgList,
              timeRange: {fromDate: payload.fromDate, toDate: payload.toDate}
            });
          })
        );
    }),
    catchError(() => of(new avgAirData.RequestFailAction()))
  );

  constructor(
    private actions$: Actions,
    private state$: Store<fromRoot.State>,
    private eventService: EventService) {
  }
}
